import { useRef, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { FileScan } from "lucide-react";
import { paths } from "@clevis/shared-types";
import { Button, Card, CardTitle, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { draftFromScan, saveExpense, scanExpenseDoc, type ScanResult } from "../lib/expenseScan";
import { ExpenseEditor, money, type ExpenseDraft, type JobOption } from "./ExpenseEditor";

// AI receiving: drop the supplier's invoice or packing slip here; Claude
// reads it (lines, costs, freight, fuel, tax) and matches lines against
// every part on order. The human reviews the matches and the costs; one tap
// receives the parts (releasing holds, stocking shelves, firing dependent
// orders) and records the expense against the jobs those parts are for,
// ready for QuickBooks.

const CONF_STYLE: Record<string, string> = {
  high: "bg-success/15 text-success",
  medium: "bg-brandorange/15 text-brandorange-dark",
  low: "bg-steel/15 text-steel",
};

export function ReceiveByInvoice({ tenantId }: { tenantId: string }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ScanResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<ExpenseDraft | null>(null);
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [done, setDone] = useState("");

  async function onFile(file: File) {
    setBusy(true);
    setError("");
    setResult(null);
    setDone("");
    try {
      const res = await scanExpenseDoc(tenantId, file);
      const picked = new Set(res.matches.filter((m) => m.confidence !== "low").map((m) => m.orderedItemId));
      const jobIds = [...new Set(res.matches.map((m) => m.item.jobId))];
      const titles = await Promise.all(
        jobIds.map(async (id) => {
          const snap = await getDoc(doc(db, paths.tenantJob(tenantId, id)));
          return { id, title: (snap.get("title") as string | undefined) ?? "Job" };
        }),
      );
      setJobs(titles);
      setSelected(picked);
      setDraft(draftFromScan(res, { useMatches: picked }));
      setResult(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function receiveSelected(): Promise<number> {
    if (!result) return 0;
    let count = 0;
    for (const m of result.matches) {
      if (!selected.has(m.orderedItemId)) continue;
      await updateDoc(doc(db, `${paths.tenantJobOrderedItems(tenantId, m.item.jobId)}/${m.orderedItemId}`), {
        status: "received",
        receivedAt: Date.now(),
      });
      count++;
    }
    return count;
  }

  async function finish(withExpense: ExpenseDraft | null) {
    if (!result) return;
    setBusy(true);
    setError("");
    try {
      const received = await receiveSelected();
      let saved = "";
      if (withExpense) {
        const res = await saveExpense(tenantId, withExpense, { source: "receiving", documentPath: result.documentPath });
        saved = `expense ${res.number} saved${res.status === "draft" ? " for approval" : ""}`;
      }
      setDone(
        [received > 0 ? `Received ${received} part${received === 1 ? "" : "s"}` : "", saved].filter(Boolean).join(" · ") ||
          "Nothing to do.",
      );
      setResult(null);
      setDraft(null);
      setSelected(new Set());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const noPrices = !!result && result.lines.length > 0 && result.lines.every((l) => l.extendedCents === null);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Receive from vendor invoice</CardTitle>
        <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy && !result} onClick={() => fileInput.current?.click()}>
          <FileScan size={14} /> Upload invoice
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
            e.target.value = "";
          }}
        />
      </div>
      <p className="mt-1 text-xs text-steel">
        Drop in the supplier's invoice or packing slip. Clevis reads the lines and costs, matches them to parts on order,
        and records the expense on the jobs they're for, ready for QuickBooks.
      </p>
      {busy && !result ? <p className="mt-2 text-xs text-brandorange-dark">Reading the document…</p> : null}
      {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      {done ? <p className="mt-2 text-xs text-success">{done}</p> : null}

      {result && draft ? (
        <div className="mt-3 space-y-3 rounded-lg bg-cream p-3">
          <p className="text-xs text-steel">
            {[result.vendorName, result.invoiceNumber ? `#${result.invoiceNumber}` : "", result.invoiceDate]
              .filter(Boolean)
              .join(" · ") || "Document read"}
            {" · "}
            {result.matches.length} of {result.openCount} open order{result.openCount === 1 ? "" : "s"} matched
            {result.totalCents !== null ? ` · printed total ${money(result.totalCents)}` : ""}
          </p>
          {result.totalMismatch ? (
            <p className="rounded bg-brandorange/10 px-2 py-1 text-xs text-ink">
              The lines and charges don't add up to the printed total. Check the costs below before saving.
            </p>
          ) : null}

          {result.matches.length > 0 ? (
            <div>
              <p className="mb-1 text-[11px] font-medium text-steel">Parts on order this receives</p>
              {result.matches.map((m) => (
                <label
                  key={m.orderedItemId}
                  className="flex cursor-pointer items-center gap-2 border-b border-steel/10 py-1.5 last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(m.orderedItemId)}
                    onChange={(e) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(m.orderedItemId);
                        else next.delete(m.orderedItemId);
                        return next;
                      })
                    }
                  />
                  <span className="min-w-0 flex-1 text-sm text-ink">
                    {m.item.quantity > 1 ? `${m.item.quantity} × ` : ""}
                    {m.item.description}
                    <span className="ml-1 block truncate text-xs text-steel">invoice line: {m.lineDescription}</span>
                  </span>
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px]", CONF_STYLE[m.confidence])}>
                    {m.confidence}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-xs text-steel">Nothing on this document matches a part you're waiting on.</p>
          )}

          {noPrices ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-steel">No prices on this document (a packing slip?), so there's no expense to record.</p>
              <Button busy={busy} disabled={selected.size === 0} onClick={() => void finish(null)}>
                Receive {selected.size} part{selected.size === 1 ? "" : "s"}
              </Button>
            </div>
          ) : (
            <div className="rounded-lg bg-white p-3">
              <p className="mb-2 text-[11px] font-medium text-steel">Expense</p>
              <ExpenseEditor
                initial={draft}
                jobs={jobs}
                showHandling
                busy={busy}
                saveLabel={selected.size > 0 ? `Receive ${selected.size} & save expense` : "Save expense"}
                onSave={(d) => void finish(d)}
                onCancel={() => {
                  setResult(null);
                  setDraft(null);
                }}
              />
              {selected.size > 0 ? (
                <button
                  className="mt-2 text-xs text-steel underline hover:text-ink"
                  onClick={() => void finish(null)}
                >
                  Receive the parts without recording an expense
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}
